<template>
  <v-card :disabled="isProcessing" :loading="isProcessing">
    <template v-slot:progress>
      <v-progress-linear
        absolute
        color="success"
        height="4"
        indeterminate
      ></v-progress-linear>
    </template>
    <v-tabs v-model="tab" class="crm-color" left dark icons-and-text>
      <v-tabs-slider></v-tabs-slider>
      <v-tab href="#tab-1">
        <v-toolbar-title>{{ $t("COMPANY_CONTACTS.TITLE") }}</v-toolbar-title>
      </v-tab>
      <v-spacer></v-spacer>
      <div class="buttons-tab">
        <v-btn
          v-if="hasPermissions('company-contacts-create')"
          class="mr-4 success"
          :disabled="isProcessing"
          @click="handleCreateClick"
        >
          {{ $t("GENERAL.LABELS.CREATE_BUTTON") }}
        </v-btn>
      </div>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <v-tab-item value="tab-1">
        <v-card flat>
          <v-card-text class="pa-0">
            <Table
              v-if="userStatePrepared"
              ref="company-contacts-table"
              height="500"
              name="company-contacts-table"
              :headers="headers"
              :hidden-headers="hiddenHeaders"
              :filters="filters"
              :blink-row-item-key="blinkRowItemKey"
              :server-data-options="{
                method: tableItemsMethod
              }"
              :items-per-page="100"
              :loading="isProcessing"
              dense
              fixed-header
              multi-sort
              filterable
            >
              <template
                v-for="(header, index) in columnHeaders"
                v-slot:[`item.${header.value}`]="{ item }"
              >
                <TableRowActions
                  v-if="header.value === 'options'"
                  :key="index"
                  :show-delete="false"
                >
                  <template #view>
                    <v-btn
                      icon
                      color="indigo"
                      @click="handleViewClick(item.item.id)"
                    >
                      <v-icon dark>
                        mdi-eye
                      </v-icon>
                    </v-btn>
                  </template>
                  <template #edit>
                    <v-btn
                      v-if="hasPermissions('company-contacts-edit')"
                      icon
                      color="primary"
                      @click="handleEditClick(item.item.id)"
                    >
                      <v-icon dark>
                        mdi-pencil
                      </v-icon>
                    </v-btn>
                  </template>
                </TableRowActions>
                <template v-else-if="header.value === 'active'">
                  <v-chip
                    x-small
                    :color="item.value ? 'success' : 'error'"
                    dark
                  >
                    {{ item.value ? $t("GENERAL.LABELS.ACTIVE") : $t("GENERAL.LABELS.INACTIVE") }}
                  </v-chip>
                </template>
                <template v-else-if="header.value === 'company_name'">
                  <router-link
                    v-if="item.item.company_id && hasPermissions('companies-view-all')"
                    :to="{ name: 'companies-view', params: { id: item.item.company_id } }"
                    class="primary--text"
                  >
                    {{ item.value || "-" }}
                  </router-link>
                  <span v-else>{{ item.value ?? "-" }}</span>
                </template>
                <template v-else-if="header.value === 'roles'">
                  <template v-if="item.item.roles?.length">
                    <v-chip
                      v-for="role in item.item.roles"
                      :key="role.id || role.value"
                      x-small
                      class="mr-1 mb-1"
                    >
                      {{ role.value }}
                    </v-chip>
                  </template>
                  <span v-else>-</span>
                </template>
                <template v-else>
                  {{ item.value ?? "-" }}
                </template>
              </template>
            </Table>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </v-card>
</template>

<script>
import Table from "@/views/partials/v-table/Table.vue";
import TableRowActions from "@/views/partials/v-table/components/TableRowActions.vue";
import { GET_COMPANY_CONTACTS } from "@/store/companyContact.module";
import { GET_COMPANIES_OPTIONS } from "@/store/companies.module";
import { GET_FIELD_TAG_OPTIONS } from "@/store/fieldTags.module";
import { SHOW_SNACK } from "@/store/snack.module";
import userStateService from "@/common/userState.service.js";

export default {
  name: "ListCompanyContacts",
  components: {
    Table,
    TableRowActions,
  },
  data() {
    return {
      tableName: "company-contacts-table",
      blinkRowItemKey: null,
      userStatePrepared: false,
      tab: "tab-1",
      isProcessing: false,
      tableItemsMethod: GET_COMPANY_CONTACTS,
      headers: [
        {
          text: "#",
          value: "id",
          align: "center",
          width: "8%",
        },
        {
          text: this.$t("GENERAL.TABLE.OPTIONS_COLUMN"),
          value: "options",
          sortable: false,
          align: "center",
          width: "10%",
        },
      ],
      hiddenHeaders: [
        {
          text: this.$t("GENERAL.LABELS.NAME"),
          value: "name",
          align: "center",
          width: "15%",
        },
        {
          text: "Name (Latin)",
          value: "name_english",
          align: "center",
          width: "15%",
        },
        {
          text: this.$t("GENERAL.LABELS.EMAIL"),
          value: "email",
          align: "center",
          width: "15%",
        },
        {
          text: this.$t("GENERAL.LABELS.PHONE"),
          value: "phone",
          align: "center",
          width: "20%",
        },
        {
          text: this.$t("GENERAL.LABELS.COMPANY"),
          value: "company_name",
          align: "center",
          width: "15%",
        },
        {
          text: this.$t("COMPANY_CONTACTS.ROLE"),
          value: "roles",
          align: "center",
          width: "12%",
          sortable: false,
        },
        {
          text: this.$t("COMPANIES.NOTES"),
          value: "notes",
          align: "center",
          width: "12%",
          sortable: false,
        },
        {
          text: this.$t("GENERAL.LABELS.STATUS"),
          value: "active",
          align: "center",
          width: "8%",
        },
      ],
      filters: [
        {
          key: "company_id",
          label: this.$t("GENERAL.LABELS.COMPANY"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: [],
        },
        {
          key: "active",
          label: this.$t("GENERAL.LABELS.STATUS"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: [
            { id: 1, label: this.$t("GENERAL.LABELS.ACTIVE") },
            { id: 0, label: this.$t("GENERAL.LABELS.INACTIVE") },
          ],
        },
        {
          key: "field_tag_id",
          label: this.$t("COMPANY_CONTACTS.ROLE"),
          type: "select",
          "item-value": "id",
          "item-text": "value",
          items: [],
        },
      ],
    };
  },
  computed: {
    columnHeaders() {
      return [...this.headers, ...this.hiddenHeaders];
    },
  },
  mounted() {
    this.blinkRowItemKey = userStateService.getState(
      `table-state.${this.tableName}.blinkRowItemKey`,
      null
    );

    const selectedHiddenHeadersKey = `table-state.${this.tableName}.selectedHiddenHeaders`;
    const selectedHiddenHeadersInitializedKey = `table-state.${this.tableName}.selectedHiddenHeadersInitializedV2`;

    if (!userStateService.getState(selectedHiddenHeadersInitializedKey, false)) {
      userStateService.setState(selectedHiddenHeadersKey, [...this.hiddenHeaders]);
      userStateService.setState(selectedHiddenHeadersInitializedKey, true);
    }

    this.applyCompanyFilterFromQuery();

    this.loadFilterOptions().finally(() => {
      this.userStatePrepared = true;
    });
  },
  methods: {
    applyCompanyFilterFromQuery() {
      const companyId = parseInt(this.$route.query.company_id, 10);

      if (!companyId) {
        return;
      }

      userStateService.setState(
        `table-state.${this.tableName}.serverFilters`,
        { company_id: [companyId] }
      );
    },
    loadFilterOptions() {
      return Promise.all([
        this.$store.dispatch(GET_COMPANIES_OPTIONS, { active: true }),
        this.$store.dispatch(GET_FIELD_TAG_OPTIONS, {
          field_tag_type_slug: "company-contact-roles",
        }),
      ])
        .then(([companiesResponse, rolesResponse]) => {
          this.filters.find((filter) => filter.key === "company_id").items = companiesResponse.data || [];
          this.filters.find((filter) => filter.key === "field_tag_id").items = rolesResponse || [];
        })
        .catch(() => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.UNEXPECTED_ERROR"),
            type: "error",
          });
        });
    },
    handleCreateClick() {
      this.$router.push({ name: "company-contacts-create" });
    },
    handleViewClick(id) {
      userStateService.setState(
        `table-state.${this.tableName}.blinkRowItemKey`,
        id
      );

      this.$router.push({ name: "company-contacts-view", params: { id } });
    },
    handleEditClick(id) {
      userStateService.setState(
        `table-state.${this.tableName}.blinkRowItemKey`,
        id
      );

      this.$router.push({ name: "company-contacts-edit", params: { id } });
    },
  },
};
</script>
